import React, { useEffect, useState, useMemo } from 'react';
import {
  getCollectionsSettings,
  updateCollectionsSettings,
  listEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
} from '../api';

const CollectionsSettings = ({ authAxios, permissions }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    collections_enabled: false,
    collections_grace_days: 7,
    collections_schedule: [0, 3, 7],
    max_collections_stages: 3,
    collections_min_interval_hours: 12,
  });
  const [templates, setTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState({ stage: 1, name: '', subject_template: '', body_template: '' });
  const canEdit = useMemo(() => (permissions?.isPartner || permissions?.isSuperuser), [permissions]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [s, t] = await Promise.all([
        getCollectionsSettings(authAxios),
        listEmailTemplates(authAxios, { category: 'collections' }),
      ]);
      setSettings(s);
      setTemplates(Array.isArray(t) ? t : (t?.results || []));
    } catch (e) {
      console.error('Failed to load collections config', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, []);

  const handleSaveSettings = async () => {
    if (!canEdit) return;
    try {
      setSaving(true);
      const payload = { ...settings };
      // Normalize schedule to integers
      payload.collections_schedule = (payload.collections_schedule || []).map(x => parseInt(x, 10)).filter(x => !isNaN(x));
      const resp = await updateCollectionsSettings(authAxios, payload);
      setSettings(resp);
    } catch (e) {
      console.error('Failed to save settings', e);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!canEdit) return;
    try {
      const data = {
        category: 'collections',
        stage: parseInt(newTemplate.stage, 10) || 1,
        name: newTemplate.name || `Collections Stage ${newTemplate.stage}`,
        subject_template: newTemplate.subject_template,
        body_template: newTemplate.body_template,
        is_active: true,
      };
      const created = await createEmailTemplate(authAxios, data);
      setTemplates(prev => [...prev, created]);
      setNewTemplate({ stage: 1, name: '', subject_template: '', body_template: '' });
    } catch (e) {
      console.error('Failed to create template', e);
    }
  };

  const handleUpdateTemplate = async (tpl, patch) => {
    if (!canEdit) return;
    try {
      const updated = await updateEmailTemplate(authAxios, tpl.id, patch);
      setTemplates(prev => prev.map(t => (t.id === tpl.id ? updated : t)));
    } catch (e) {
      console.error('Failed to update template', e);
    }
  };

  const handleDeleteTemplate = async (tpl) => {
    if (!canEdit) return;
    try {
      await deleteEmailTemplate(authAxios, tpl.id);
      setTemplates(prev => prev.filter(t => t.id !== tpl.id));
    } catch (e) {
      console.error('Failed to delete template', e);
    }
  };

  const renderTemplateRow = (tpl) => (
    <tr key={tpl.id} className="border-b">
      <td className="p-2 text-sm">{tpl.stage || '-'}</td>
      <td className="p-2 text-sm">
        <input
          type="text"
          className="w-full border rounded p-1"
          value={tpl.name || ''}
          disabled={!canEdit}
          onChange={e => handleUpdateTemplate(tpl, { name: e.target.value })}
        />
      </td>
      <td className="p-2">
        <input
          type="text"
          className="w-full border rounded p-1 text-sm"
          value={tpl.subject_template || ''}
          disabled={!canEdit}
          onChange={e => handleUpdateTemplate(tpl, { subject_template: e.target.value })}
        />
      </td>
      <td className="p-2">
        <textarea
          className="w-full border rounded p-1 text-sm"
          rows={3}
          value={tpl.body_template || ''}
          disabled={!canEdit}
          onChange={e => handleUpdateTemplate(tpl, { body_template: e.target.value })}
        />
      </td>
      <td className="p-2 text-center">
        <input
          type="checkbox"
          checked={!!tpl.is_active}
          disabled={!canEdit}
          onChange={e => handleUpdateTemplate(tpl, { is_active: e.target.checked })}
        />
      </td>
      <td className="p-2 text-right">
        {canEdit && (
          <button className="text-red-600 text-sm" onClick={() => handleDeleteTemplate(tpl)}>Delete</button>
        )}
      </td>
    </tr>
  );

  if (loading) {
    return <div className="p-4">Loading collections settings…</div>;
  }

  return (
    <div className="p-4 space-y-6">
      <h2 className="text-xl font-semibold">Collections Settings</h2>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.collections_enabled}
            disabled={!canEdit}
            onChange={e => setSettings({ ...settings, collections_enabled: e.target.checked })}
          />
          <span>Enable Collections</span>
        </label>

        <label className="flex flex-col text-sm">
          Grace Days
          <input
            type="number"
            className="border rounded p-1"
            value={settings.collections_grace_days}
            disabled={!canEdit}
            onChange={e => setSettings({ ...settings, collections_grace_days: parseInt(e.target.value || 0, 10) })}
          />
        </label>

        <label className="flex flex-col text-sm">
          Schedule (day offsets)
          <input
            type="text"
            className="border rounded p-1"
            value={(settings.collections_schedule || []).join(',')}
            disabled={!canEdit}
            onChange={e => setSettings({ ...settings, collections_schedule: e.target.value.split(',').map(s => s.trim()) })}
          />
        </label>

        <label className="flex flex-col text-sm">
          Max Stages
          <input
            type="number"
            className="border rounded p-1"
            value={settings.max_collections_stages}
            disabled={!canEdit}
            onChange={e => setSettings({ ...settings, max_collections_stages: parseInt(e.target.value || 1, 10) })}
          />
        </label>
        <label className="flex flex-col text-sm">
          Min Interval (Hours)
          <input
            type="number"
            className="border rounded p-1"
            value={settings.collections_min_interval_hours}
            disabled={!canEdit}
            onChange={e => setSettings({ ...settings, collections_min_interval_hours: parseInt(e.target.value || 12, 10) })}
          />
          <p className="text-xs text-gray-500 mt-1">Rate limit between successive dunning emails (prevents spam).</p>
        </label>
      </div>

      {canEdit && (
        <div>
          <button
            className="bg-blue-600 text-white px-3 py-1 rounded disabled:opacity-50"
            disabled={saving}
            onClick={handleSaveSettings}
          >
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      )}

      <h3 className="text-lg font-semibold pt-4">Collections Email Templates</h3>
      <div className="overflow-auto">
        <table className="min-w-full border">
          <thead>
            <tr className="bg-gray-50 text-left text-sm">
              <th className="p-2">Stage</th>
              <th className="p-2">Name</th>
              <th className="p-2">Subject</th>
              <th className="p-2">Body</th>
              <th className="p-2 text-center">Active</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {templates
              .filter(t => t.category === 'collections')
              .sort((a, b) => (a.stage || 0) - (b.stage || 0))
              .map(renderTemplateRow)}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div className="border rounded p-3 space-y-2">
          <div className="font-medium">Add Template</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="flex flex-col text-sm">
              Stage
              <input
                type="number"
                className="border rounded p-1"
                value={newTemplate.stage}
                onChange={e => setNewTemplate({ ...newTemplate, stage: parseInt(e.target.value || 1, 10) })}
              />
            </label>
            <label className="flex flex-col text-sm md:col-span-3">
              Name
              <input
                type="text"
                className="border rounded p-1"
                value={newTemplate.name}
                onChange={e => setNewTemplate({ ...newTemplate, name: e.target.value })}
              />
            </label>
            <label className="flex flex-col text-sm md:col-span-2">
              Subject
              <input
                type="text"
                className="border rounded p-1"
                value={newTemplate.subject_template}
                onChange={e => setNewTemplate({ ...newTemplate, subject_template: e.target.value })}
              />
            </label>
            <label className="flex flex-col text-sm md:col-span-4">
              Body
              <textarea
                className="border rounded p-1"
                rows={4}
                value={newTemplate.body_template}
                onChange={e => setNewTemplate({ ...newTemplate, body_template: e.target.value })}
              />
            </label>
          </div>
          <button className="bg-green-600 text-white px-3 py-1 rounded" onClick={handleCreateTemplate}>
            Add Template
          </button>
        </div>
      )}
    </div>
  );
};

export default CollectionsSettings;
